import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation } from 'react-router';
import { AppUpdateBar, isTyping, reloadOnMissingCode, useAppUpdate } from '@crave/ui';
import '@crave/ui/tokens.css';
import { App } from './App.tsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 5_000 } },
});

if (import.meta.env.PROD) reloadOnMissingCode();

/**
 * A newer deploy never interrupts the cart, checkout or a payment. The site reloads when the
 * customer goes back to the menu (the cart is kept), and shows a small bar on other pages.
 */
function UpdateWatcher() {
  const { pathname, search } = useLocation();
  const { action, reload } = useAppUpdate({
    policy: 'order',
    currentVersion: import.meta.env['VITE_BUILD_ID'] as string | undefined,
    enabled: import.meta.env.PROD,
    path: pathname,
    search,
    isBusy: () => queryClient.isMutating() > 0 || isTyping(),
  });
  // The menu's cart button sits at the bottom of the screen, so the bar goes on top.
  return action === 'show-bar' ? <AppUpdateBar onReload={reload} placement="top" /> : null;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {window.location.pathname.startsWith('/reset-password') ? <ResetPasswordPage /> : <App />}
        <UpdateWatcher />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
